import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Wordmark } from "@/components/Wordmark";
import { ThemeToggle } from "@/components/ThemeToggle";

const navLinks = [
  { to: "/deals",   label: "Browse Deals" },
  { to: "/faq",     label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function PublicShell({ children, transparentHeader = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-surface-alt flex flex-col">
      <header
        className={cn(
          "sticky top-0 z-40 transition-colors",
          transparentHeader && location.pathname === "/"
            ? "bg-surface-alt/85 backdrop-blur-md border-b border-line/60"
            : "bg-surface border-b border-line"
        )}
      >
        <div className="max-w-site mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="GCBC home">
            <Wordmark size="md" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn(
                    "group relative font-brand text-body-sm font-semibold tracking-tight transition-colors",
                    isActive ? "text-brand-700" : "text-ink hover:text-brand-700"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={cn(
                        "absolute -bottom-1.5 left-0 right-0 h-0.5 bg-brand-700 origin-center transition-transform duration-300",
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      )}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/login"
              className="relative px-4 py-2 text-body-sm font-semibold text-ink-muted hover:text-ink transition-colors group"
              data-testid="header-signin"
            >
              Sign in
              <span className="absolute left-4 right-4 bottom-1 h-0.5 bg-brand-700 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
            </Link>
            <Link
              to="/apply"
              data-testid="header-apply"
              className="group relative inline-flex items-center gap-2 h-10 pl-5 pr-1.5 rounded-full bg-ink hover:bg-brand-700 text-surface text-body-sm font-semibold transition-all duration-300 shadow-[0_4px_14px_rgba(15,23,42,0.18)] hover:shadow-[0_6px_20px_rgba(15,118,110,0.32)]"
            >
              <span>Apply to join</span>
              <span className="size-7 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45">
                <ArrowUpRight className="size-3.5" strokeWidth={2.4} />
              </span>
            </Link>
          </div>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden inline-flex items-center justify-center size-9 rounded-md text-ink hover:bg-surface-alt" aria-label="Open menu">
                <Menu className="size-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <div className="p-5 border-b border-line flex items-center justify-between">
                <Wordmark size="sm" />
                <button onClick={() => setOpen(false)} className="size-8 rounded-md hover:bg-surface-alt grid place-items-center" aria-label="Close"><X className="size-4" /></button>
              </div>
              <div className="p-3 flex flex-col">
                {navLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} className={({ isActive }) => cn("px-3 py-3 rounded-md text-body text-ink", isActive && "bg-brand-50 text-brand-800 font-semibold")} onClick={() => setOpen(false)}>{l.label}</NavLink>
                ))}
                <div className="h-px bg-line my-3" />
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md text-body text-ink">Sign in</Link>
                <Link to="/admin/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md text-caption text-ink-hint" data-testid="mobile-admin-signin">Admin sign-in</Link>
                <Link to="/apply" onClick={() => setOpen(false)} className="mx-3 mt-2">
                  <Button className="w-full bg-brand-700 hover:bg-brand-800 text-white">Apply to join</Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-surface border-t border-line">
      <div className="max-w-site mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <Wordmark size="md" />
          <p className="mt-4 text-body-sm text-ink-muted max-w-xs leading-relaxed">
            A members-only buying group with vetted supply and transparent rates. Cards in, cash out — by ACH, in days.
          </p>
          <p className="mt-6 text-caption text-ink-hint">
            © {new Date().getFullYear()} Gift Card Buyers Club. Operated in the United States.
          </p>
        </div>

        <FooterCol title="Product" links={[
          { to: "/deals",       label: "Live deals" },
          { to: "/apply",       label: "Apply to join" },
          { to: "/login",       label: "Member sign-in" },
          { to: "/admin/login", label: "Admin sign-in" },
        ]} />
        <FooterCol title="Company" links={[
          { to: "/faq",     label: "FAQ" },
          { to: "/contact", label: "Contact" },
        ]} />
        <FooterCol title="Legal" links={[
          { to: "/faq",     label: "Terms" },
          { to: "/faq",     label: "Privacy" },
          { to: "/faq",     label: "Disclosures" },
        ]} />
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-eyebrow uppercase text-ink-muted mb-3">{title}</h4>
      <ul className="space-y-2">
        {links.map((l, i) => (
          <li key={i}>
            <Link to={l.to} className="text-body-sm text-ink hover:text-brand-700 transition-colors">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PublicShell;
